import React, { useState, useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";

import { Mic, StopCircle, Send, Fullscreen } from 'lucide-react';

import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskType
} from "@heygen/streaming-avatar";
import { useToken } from '../components/items/UseToken';
import { IconButton } from "@mui/material";
import ChatImage from './chat.mov';
import MarkdownRenderer from "./components/MarkdownRenderer";



function getChatbotHubUrl() {
    const { protocol, hostname } = window.location;

    // DEV: localhost lub 127.0.0.1
    if (hostname.includes('localhost') || hostname.startsWith('127.')) {
        return 'http://localhost:5000/chatbothub';
    }

    const parts = hostname.split('.');
    const baseDomain = parts.slice(1, -1).join('.');
    return `https://assistant.${baseDomain}.app/chatbothub`;
}


export default function ChatWidget({ chatAgent, clientId}) {
    const { token } = useToken();


    const publicChatbotHub = getChatbotHubUrl();
    const [selectedAgent, setSelectedAgent] = useState(null);
    const generateUUID = () => crypto.randomUUID();
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState({});
    const [imagesAtt, setImagesAtt] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const messageInputRef = useRef();
    const chatMessagesRef = useRef();
    const connection = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null); // State for modal image
    const [displayImages, setDisplayImages] = useState(true);

    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioContextRef = useRef(null);
    const workletNodeRef = useRef(null);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [useMarkdown, setUseMarkdown] = useState(true);


    const fileInputRef = useRef(null);
// avatar
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [avatarFullScreen, setAvatarFullscreen] = useState(false);
    const videoElement = useRef(null);
    let avatar  = null;
    let sessionData = null;

// Helper function to fetch access token


// Initialize streaming avatar session
async function initializeAvatarSession() {
    const avatarToken = JSON.parse(selectedAgent.avatar_details).key;
    avatar = new StreamingAvatar({ token:avatarToken });

  avatar.on(StreamingEvents.STREAM_READY, handleStreamReady);
  avatar.on(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
  
  sessionData = await avatar.createStartAvatar({
    quality: AvatarQuality.High,
    avatarName: JSON.parse(chatAgent.avatar_details).avatar_id,
  });

  console.log("Session data:", sessionData);

  
}

// Handle when avatar stream is ready
function handleStreamReady(event) {
  if (event.detail && videoElement) {
    console.log(event);
    videoElement.current.srcObject = event.detail;
    videoElement.current.onloadedmetadata = () => {
      videoElement.current.play().catch(console.error);
      setAvatarLoaded(true);
    };
  } else {
    console.error("Stream is not available");
  }
}

// Handle stream disconnection
function handleStreamDisconnected() {
  console.log("Stream disconnected");
  if (videoElement) {
    videoElement.current.srcObject = null;
  }

}

// End the avatar session
async function terminateAvatarSession() {
  if (!avatar || !sessionData) return;

  await avatar.stopAvatar();
  videoElement.current.srcObject = null;
  avatar = null;
}

// Handle speaking event
async function handleSpeak(input) {
  if (sessionData && input) {
    await avatar.speak({
      text: input,
      task_type: "repeat"
    });
  }
    }
    async function handleAvatarBargeIn() {
        if (sessionData) {
            await avatar.interrupt();
        }
    }


    // avatar end
    const handleSignalRInit = async () => {
        const newSessionId = generateUUID();
        console.log(newSessionId);
        const deployedApiKey = await fetch(`${clientId}/api/deployagentwidget/by-guid?agent_guid=${chatAgent.guid}`, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            },
        });
        const jsonDeployed = await deployedApiKey.json();
        const auth = await fetch(`${clientId}/api/widgetaccess`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                apiKey: jsonDeployed.apiKey,
            }),
        });
        const json = await auth.json();
        const agentDetails = { "apiKey": jsonDeployed.apiKey, "name": json.name, "token": json.token, "guid": "widget", "voice": json.voice, "picture": json.picture, "avatar_type": json.avatar_type, "avatar_details": json.avatar_details, "title": json.title }
        connection.current = new signalR.HubConnectionBuilder()
            .withUrl(`${publicChatbotHub}`, {
                headers: {
                  "X-Sophona-Session": newSessionId,
                }
              })
              .build();
        setSessionId(newSessionId); // Set session ID
        setSelectedAgent(agentDetails)


    };
    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        const promises = files.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve({
                        base64: reader.result,   // 🟣 This is your actual base64 string
                        url: URL.createObjectURL(file) // optional for preview
                    });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file); // ✅ Important: read as Data URL
            });
        });

        const newBase64Images = await Promise.all(promises);
        setImagesAtt([...imagesAtt, ...newBase64Images]);
    };

    const handleRemoveImage = (index) => {
        const updated = [...imagesAtt];
        updated.splice(index, 1);
        setImagesAtt(updated);
    };

    const handleImageClick = (imageSrc) => {
        setSelectedImage(imageSrc); // Set the selected image for
    };

    const closeModal = () => {
        setSelectedImage(null); // Close modal
    };

    useEffect(() => {

        const initAgent = async () => {
            if (selectedAgent != null && selectedAgent.avatar_type === "heygen" && !avatarLoaded) {
                await initializeAvatarSession();
            }
            if ((selectedAgent != null && selectedAgent.avatar_type === "basic") || selectedAgent != null && selectedAgent.avatar_type === "heygen" && sessionData) {
                connection.current.start()
                    .then(async () => {
                        console.log("SignalR connected");
                        startSession(selectedAgent.token);
                        setIsConnected(true);


                    })
                    .catch((err) => {
                        console.error("SignalR connection error:", err);
                    });

                connection.current.on("ReceiveMessage", async (response, messageType) => {
                    console.log(response);
                    if (messageType === "barge") {
                        setIsAgentTyping(false);

                        bargeIn();
                       handleAvatarBargeIn();
                    }
                    else if (messageType === "log") {
                        console.log(response);
                    }

                    else {
                        setIsAgentTyping(false);
                        addMessageToChat(response, messageType);
                        if (messageType === "avatar_text" && selectedAgent.avatar_type === "heygen") {
                            handleSpeak(response);
                        }
                    }
                    return true;
                });


                connection.current.on("ReceiveAudio", async (incomingAudio) => {
                    try {
                        if (selectedAgent.avatar_type === "basic") {
                            console.log("Audio received from bot " + incomingAudio);
                            handleIncomingChunk(incomingAudio);
                        }
                        return true;

                    } catch (err) {
                        console.error("ReceiveAudio error:", err);
                        return false;
                    }



                });


                setMessages((prev) => ({
                    ...prev,
                    [selectedAgent.guid]: prev[selectedAgent.guid] || [],
                }));
            }

        }

        initAgent();

    }, [selectedAgent, avatarLoaded])
    // Initialize SignalR connection
    useEffect(() => {
        // jeśli agent się nie zmienił lub jest null — nie rób nic
        if (!chatAgent) return;

        console.log("🔄 Chat agent changed:", chatAgent);

        // zatrzymaj poprzednie połączenie (jeśli istnieje)
        const stopPreviousSession = async () => {
            try {
                if (connection.current) {
                    console.log("🛑 Disconnecting old SignalR session...");
                    await connection.current.stop();
                }

                if (selectedAgent?.avatar_type === "heygen") {
                    console.log("🛑 Stopping old Heygen avatar...");
                    await terminateAvatarSession();
                }
            } catch (err) {
                console.warn("Cleanup error:", err);
            }

            // wyczyść stany
            setIsConnected(false);
            setMessages({});
            setImagesAtt([]);
            setSelectedImage(null);
            setAvatarLoaded(false);
        };

        const startNewSession = async () => {
            await stopPreviousSession();
            await handleSignalRInit(); // ta funkcja ustawi selectedAgent, sessionId i połączenie
        };

        startNewSession();

        // cleanup przy unmount lub kolejnym chatAgent
        return () => {
            stopPreviousSession();
        };
    }, [chatAgent]);




    // Start session when SignalR is connected and agent is selected
   

    const startSession = async () => {
        try {
            
            const { protocol, hostname, port } = window.location;
            const cmdUrl = port
                ? `${protocol}//${hostname}:${port}`   
                : `${protocol}//${hostname}`;         

            await connection.current.invoke("StartWidget", sessionId, selectedAgent.apiKey, cmdUrl, selectedAgent.token, null, null);
            console.log(`Session started: ${sessionId}`);
    
        } catch (error) {
            console.error("Error starting session :", error);
        }
    };
  



    const sendMessage = async () => {
        let message = messageInputRef.current.value.trim();
        if ((message || imagesAtt.length > 0) && sessionId) {
            if (message) {
                setMessages((prev) => ({
                    ...prev,
                    [selectedAgent.guid]: [
                        ...(prev[selectedAgent.guid] || []),
                        { message, type: "sent" },
                    ],
                }));
            }
            
            if (imagesAtt.length > 0) {
                
                setMessages((prev) => ({
                    ...prev,
                    [selectedAgent.guid]: [
                        ...(prev[selectedAgent.guid] || []),
                        ...imagesAtt.map((img) => ({
                            message: img.base64,
                            type: "sent",
                            isImage: true
                        })),
                    ],
                }));
            }
            messageInputRef.current.value = "";

            try {
                setImagesAtt([]);
                setIsAgentTyping(true);
                if (imagesAtt.length > 0) {
                    const payload = imagesAtt.map(img => img.base64);
                    console.log(payload);
                    await connection.current.invoke("SendMessageWithImages", sessionId, message, payload);

                }
                else {
                    await connection.current.invoke("SendMessage", sessionId, message);

                }
                

            } catch (error) {
                console.error("Failed to send message:", error);
            }
        }
    };

    

    const addMessageToChat = (message, messageType) => {
        if (!selectedAgent) return;

        setMessages((prev) => {
            const agentId = selectedAgent.guid;
            const existing = prev[agentId] || [];

            // jeśli to "received" (np. streaming delta)
            if (messageType === "received") {
                const isMdStart = message.trimStart().startsWith("```");

                const agentId = selectedAgent.guid;
                const existing = prev[agentId] || [];

                const lastMessage = existing[existing.length - 1];

                // jeśli zaczyna się od code fence -> nowa wiadomość markdown
                if (isMdStart) {

                    return {
                        ...prev,
                        [agentId]: [
                            ...existing,
                            { message: message, type: "received", isMarkdown: true }
                        ],
                    };
                }

                // jeśli poprzednia była markdown – dopisuj
                if (lastMessage && lastMessage.isMarkdown) {
                    const updated = [...existing];
                    updated[updated.length - 1] = {
                        ...lastMessage,
                        message: lastMessage.message + message,
                    };
                    return { ...prev, [agentId]: updated };
                }
                // jeśli ostatnia wiadomość też jest "received" → dopisz do niej tekst
                else if (lastMessage && lastMessage.type === "received") {
                    const updatedMessages = [...existing];
                    updatedMessages[updatedMessages.length - 1] = {
                        ...lastMessage,
                        message: (lastMessage.message || "") + message,
                    };
                    return { ...prev, [agentId]: updatedMessages };
                }

                // jeśli nie ma żadnej wiadomości lub poprzednia była innego typu — dodaj nową
                return {
                    ...prev,
                    [agentId]: [...existing, { message, type: messageType }],
                };
            }

            // jeśli to nowa wiadomość od użytkownika lub typ inny niż received
            if (["sent", "image"].includes(messageType)) {
                if (messageType === "image" && !displayImages) return prev;
                return {
                    ...prev,
                    [agentId]: [...existing, { message, type: messageType }],
                };
            }

            


            return prev;
        });
    };

    const toggleDisplayImagesMode = async () => {
        setDisplayImages((prev) => !prev);
       
    };

    const startVoiceSession = async () => {
        if (!isConnected || !selectedAgent) return;

         startRecording();
         initializeAudioContext();
        
        connection.current.invoke("StartVoiceService", sessionId, "Web");
        
        
        console.log("Voice service started");
        
    };

    const startRecording = async () => {
        setIsRecording(true);

        try {
            console.log(selectedAgent.voice);
            var sample = 16000;
            if (selectedAgent.voice.startsWith("Realtime")) {
                sample = 24000;
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const audioContext = new AudioContext({ sampleRate: sample }); // Set sample rate to 16kHz
            const source = audioContext.createMediaStreamSource(stream);

            // Initialize the AudioWorkletNode
            await audioContext.audioWorklet.addModule("workletProcessor.js");

    const workletNode = new AudioWorkletNode(audioContext, 'microphone-processor');

    // Handle data coming from the processor
    workletNode.port.onmessage = (event) => {
      const audioChunk = event.data;
      const base64Chunk = arrayBufferToBase64(audioChunk);
      sendAudioChunk(base64Chunk);
    };

    source.connect(workletNode);
    workletNode.connect(audioContext.destination);

            // Save references for cleanup
            mediaRecorderRef.current = { workletNode, audioContext, stream };
        } catch (error) {
            console.error("Error starting recording:", error);
        }
    };

    const stopRecording = async () => {
      if (mediaRecorderRef.current) {
        const { workletNode, audioContext, stream } = mediaRecorderRef.current;
    
        // Properly disconnect the Worklet Node
        if (workletNode && audioContext) {
          workletNode.port.postMessage({ command: 'stop' }); // Optional: If you want to inform the processor
          workletNode.disconnect();
        }
    
        // Close the AudioContext
        if (audioContext) {
          await audioContext.close();
        }
    
        // Stop all the media tracks
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      }
    
      // Also, close the main audio context reference if it exists
      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
    
      await connection.current.invoke("StopVoiceService", sessionId);
      console.log("Voice service stopped");
      setIsRecording(false);
    };
    

    const sendAudioChunk = async (audioChunk) => {
        try {
            if (sessionId && connection.current) {
                await connection.current.invoke("SendAudioChunk", sessionId, audioChunk);
                console.log("Audio chunk sent");
            }
        } catch (error) {
            console.error("Error sending audio chunk:", error);
        }
    };

    // Convert Float32Array to Linear16 PCM
    const convertToLinear16 = (inputBuffer) => {
        const buffer = new ArrayBuffer(inputBuffer.length * 2); // 16-bit PCM = 2 bytes per sample
        const view = new DataView(buffer);

        for (let i = 0; i < inputBuffer.length; i++) {
            let sample = inputBuffer[i];
            if (sample > 1) sample = 1;
            else if (sample < -1) sample = -1;

            sample = sample * 32767; // Scale to 16-bit range
            view.setInt16(i * 2, sample, true); // Little-endian
        }

        return buffer;
    };

    // Convert ArrayBuffer to Base64
    const arrayBufferToBase64 = (buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    const initializeAudioContext = async () => {
  audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
    sampleRate: 24000,
  });

  // Register the worklet
  await audioContextRef.current.audioWorklet.addModule('workletProcessor.js');

   workletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');
   workletNodeRef.current.connect(audioContextRef.current.destination);

  console.log("Initialized AudioContext for playback.");
};




    const handleIncomingChunk = async (base64Chunk) => {
        try {
            const audioBuffer = decodeBase64ToArrayBuffer(base64Chunk);
            const wavAudio = addWavHeader(audioBuffer); // Dodaj nagłówek WAV

            // Dekodowanie audio i dodanie do kolejki
            const decodedBuffer = await audioContextRef.current.decodeAudioData(wavAudio);

            const rawData = decodedBuffer.getChannelData(0); // Mono kanał
            const chunk = new Float32Array(rawData); // Skopiuj dane
            workletNodeRef.current.port.postMessage(chunk);

            // audioBufferQueueRef.current.push(decodedBuffer);
            // playNextInQueue(); // Spróbuj odtworzyć kolejkę
        } catch (error) {
            console.error("Error processing audio chunk:", error);
        }
    };
    const bargeIn = async () => {
        if (workletNodeRef.current != null)
        workletNodeRef.current.port.postMessage({ type: 'stop' });


    };




    const decodeBase64ToArrayBuffer = (base64) => {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    };

    const addWavHeader = (audioBuffer, sampleRate = 24000, channels = 1) => {
        const bufferLength = audioBuffer.byteLength;
        const header = new ArrayBuffer(44);
        const view = new DataView(header);

        /* RIFF header */
        writeString(view, 0, "RIFF");
        view.setUint32(4, 36 + bufferLength, true);
        writeString(view, 8, "WAVE");
        writeString(view, 12, "fmt ");
        view.setUint32(16, 16, true); // Format chunk length
        view.setUint16(20, 1, true); // PCM format
        view.setUint16(22, channels, true); // Channels
        view.setUint32(24, sampleRate, true); // Sample rate
        view.setUint32(28, sampleRate * channels * 2, true); // Byte rate
        view.setUint16(32, channels * 2, true); // Block align
        view.setUint16(34, 16, true); // Bits per sample
        writeString(view, 36, "data");
        view.setUint32(40, bufferLength, true);

        const wavBuffer = new Uint8Array(44 + bufferLength);
        wavBuffer.set(new Uint8Array(header), 0);
        wavBuffer.set(new Uint8Array(audioBuffer), 44);

        return wavBuffer.buffer;
    };

    const writeString = (view, offset, string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };


    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages[selectedAgent?.guid]]);

    const TypingIndicator = () => {
      return (
        <div className="flex bg-white items-center space-x-1 w-10 h-4 m-6 rounded-md">
          <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
          <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
          <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
        </div>
      );
    };


   

    return (
        isConnected || selectedAgent != null && selectedAgent.avatar_type === "heygen" ? (
       <> 
                <div className="flex">
                    {!avatarLoaded && chatAgent && selectedAgent.avatar_type === "heygen" &&
                        <div className="relative w-full">
                            <img
                                src={JSON.parse(chatAgent.avatar_details).normal_preview}
                                alt="Agent Avatar"
                                className="rounded-[10px] object-cover basicAvatar"
                            />

                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm text-white">
                                <div className="relative flex items-center justify-center">
                                    <div className="absolute w-12 h-12 border-4 border-white/30 rounded-full animate-ping"></div>
                                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                                </div>
                                <p className="mt-4 text-sm font-medium opacity-80">Connecting to avatar...</p>
                            </div>
                        </div>

                        }
                    <video ref={videoElement} className={!avatarLoaded ? "hidden" : avatarFullScreen ? "block fullAvatarVideoCMD" : "block smallAvatarVideo"} id="avatarVideo" autoPlay playsInline></video>
       {avatarLoaded && <button
  onClick={() => setAvatarFullscreen((prevValue)=> !prevValue)}
  className=" absolute  m-2 p-2 rounded-full bg-black-100 hover:bg-black-200 text-black-600 border border-black-300 shadow"
>
  <Fullscreen className="w-5 h-5" />
</button>}
{selectedAgent.avatar_type === "basic" &&
  <div className="relative w-full">
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                style={{
                                    width: "100%",
                                    height: "15vh",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    //filter: "brightness(1) blur(5px)", // lekko przyciemnione i rozmyte
                                    zIndex: 100,
                                }}
                            >
                                <source src={ChatImage} type="video/mp4" />
                                <source src={ChatImage} type="video/quicktime" />
                                Your browser does not support the video tag.
                            </video>
        
        <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-gray-800 opacity-70">
            <h3 class="text-xl text-white font-bold">
                {selectedAgent.name}</h3>
                                <p className="mt-2 text-sm text-gray-300">{selectedAgent.title}
            </p>
        </div>
    </div>
  
    }
       </div>

<div className="flex p-[5%] flex-col min-h-0 h-full w-full space-y-4 text-sm bg-white  text-gray-800 ">

{/* Messages */}
<div
  ref={chatMessagesRef}
  className="flex-1 px-2 space-y-2 bg-white rounded-md"
  style={{ overflowY: "auto", minHeight: 0 }}
>


  {selectedAgent && messages[selectedAgent.guid]?.length > 0 ? (
    messages[selectedAgent.guid].map((msg, index) => {
      const isUser = msg.type === 'sent';
      return (
          <div
              key={index}
              className={`flex items-start gap-2 ${isUser ? 'justify-end' : 'justify-start'} px-2`}
          >
              {/* Avatar tylko dla agenta */}
              {!isUser && msg.message && msg.message != "```" && (
                  <img
                      src={(selectedAgent.picture.startsWith("data:image") || selectedAgent.picture.startsWith("http")) ? selectedAgent.picture : `data:image/png;base64,${selectedAgent.picture}`}
                      alt="Agent Avatar"
                      className="w-8 h-8 rounded-full object-cover mt-1"
                  />
              )}

              {msg.message && msg.message != "```" && <div
                  className={`
    max-w-[75%] px-4 py-2 rounded-xl text-sm leading-relaxed shadow-sm
    break-words overflow-x-hidden  max-w-full
    ${isUser
                          ? 'bg-black text-white rounded-bl-md'
                          : !msg.isMarkdown ? 'bg-gray-200 text-gray-900 rounded-bl-md' : 'rounded-bl-md'}
  `}
              >

                  {msg.isImage ? (
                      <>
                          <img
                              src={
                                  msg.message.startsWith('data:image')
                                      ? msg.message
                                      : `data:image/png;base64,${msg.message}`
                              }
                              alt="Image"
                              className="rounded-md max-w-full max-h-60 cursor-pointer"
                              onClick={() => handleImageClick(msg.message)}
                          />
                      </>
                  ) : useMarkdown && msg.isMarkdown ? (
                      msg.message != "```" && <MarkdownRenderer content={msg.message.replace("```markdown", "")} />
                  ) : (msg.message != "```" &&
                      <span className="whitespace-pre-line">{msg.message}</span>
                  )}

              </div>}
  
</div>


      );
    }
  )
  
  ) : (
    // 
    <TypingIndicator></TypingIndicator>
  )}
  {isAgentTyping && <TypingIndicator/>}
</div>


{/* Image preview */}
<div className="image-attachment-container">
  <div className="flex flex-wrap gap-2">
    {imagesAtt.map((img, idx) => (
      <div key={idx} className="relative">
        <img src={img.url} alt={`preview-${idx}`} className="w-16 h-16 object-cover rounded-md" />
        <button
          className="absolute top-0 right-0 bg-white bg-opacity-50 text-white text-xs px-1 rounded-bl hover:bg-opacity-70"
          onClick={() => handleRemoveImage(idx)}
        >
          ✖
        </button>
      </div>
    ))}
  </div>

  <button
    className="upload-btn mt-2 text-xl w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-md"
    onClick={() => fileInputRef.current.click()}
  >
    +
  </button>

  <input
    type="file"
    ref={fileInputRef}
    style={{ display: "none" }}
    accept="image/*"
    multiple
    onChange={handleImageChange}
  />
</div>

{/* Input and controls */}
<div className="flex items-end gap-2 mt-2">
  <input
    ref={messageInputRef}
    type="text"
    placeholder={selectedAgent ? `Chat with ${selectedAgent.name}...` : "Select an agent to start chatting"}
    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
    className="flex-1 rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500 bg-white  text-gray-900 "
  />

  <div className="flex items-center gap-1">

                            <IconButton
                                onClick={startVoiceSession}
                                disabled={isRecording}
                                color="primary"
                                size="large"
                            >
                                <Mic />
                            </IconButton>

                            <IconButton
                                onClick={stopRecording}
                                disabled={!isRecording}
                                color="secondary"
                                size="large"
                            >
                                <StopCircle />
                            </IconButton>

                            <IconButton
                                onClick={sendMessage}
                                color="success"
                                size="large"
                            >
                                <Send />
                            </IconButton>


  </div>
</div>

{/* Modal for enlarged image */}
{selectedImage && (
  <div
    className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
    onClick={closeModal}
  >
    <img
      src={selectedImage.startsWith('data:image') ? selectedImage : `data:image/png;base64,${selectedImage}`}
      alt="Enlarged"
      className="max-w-[90%] max-h-[90%] rounded-xl shadow-xl"
    />
  </div>
)}
</div>
</> 


    )
    :
    (<div className="flex items-center justify-center w-full h-full bg-white">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        <p className="mt-4 text-lg text-gray-500">Loading, please wait...</p>
      </div>
    </div>)
  );
}
